
import { useEffect, useState } from 'react';
import { X, Circle, UserMinus, AlertCircle, UserPlus, Wifi, WifiOff, CheckCircle, Clock } from 'lucide-react';

interface Toast {
  id: string;
  message: string;
  type: 'recording' | 'peer-left' | 'peer-joined' | 'connected' | 'connecting' | 'error' | 'success' | 'time-warning';
}

let toastCounter = 0;
const toastCallbacks: ((toast: Toast) => void)[] = [];

export function showImportantToast(message: string, type: Toast['type']) {
  const toast: Toast = {
    id: `toast-${++toastCounter}`,
    message,
    type,
  };
  toastCallbacks.forEach(cb => cb(toast));
}

export function VideoCallToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const callback = (toast: Toast) => {
      setToasts(prev => [...prev, toast]);
      
      // Auto-dismiss after 4 seconds
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toast.id));
      }, 4000);
    };

    toastCallbacks.push(callback);

    return () => {
      const index = toastCallbacks.indexOf(callback);
      if (index > -1) toastCallbacks.splice(index, 1);
    };
  }, []);

  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[200] flex flex-col items-center pt-4 px-4 pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="pointer-events-auto w-full max-w-2xl mb-2 animate-in slide-in-from-top-4 duration-300"
        >
          <div className="bg-gray-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 border border-white/10">
            {/* Icon */}
            <div className="flex-shrink-0">
              {toast.type === 'recording' && (
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Circle className="h-5 w-5 text-red-400 fill-red-400" strokeWidth={2} />
                </div>
              )}
              {toast.type === 'peer-joined' && (
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-green-400" strokeWidth={2} />
                </div>
              )}
              {toast.type === 'peer-left' && (
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <UserMinus className="h-5 w-5 text-orange-400" strokeWidth={2} />
                </div>
              )}
              {toast.type === 'connected' && (
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Wifi className="h-5 w-5 text-green-400" strokeWidth={2} />
                </div>
              )}
              {toast.type === 'connecting' && (
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <WifiOff className="h-5 w-5 text-blue-400 animate-pulse" strokeWidth={2} />
                </div>
              )}
              {toast.type === 'error' && (
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-red-400" strokeWidth={2} />
                </div>
              )}
              {toast.type === 'success' && (
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-400" strokeWidth={2} />
                </div>
              )}
              {toast.type === 'time-warning' && (
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-orange-400" strokeWidth={2} />
                </div>
              )}
            </div>
            
            {/* Message */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-relaxed">{toast.message}</p>
            </div>
            
            {/* Dismiss button */}
            <button
              onClick={() => dismissToast(toast.id)}
              className="flex-shrink-0 p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}